import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, CircularProgress, Typography} from "@mui/material";
import {useNavigate, useParams} from "react-router";
import {makeCresAxiosFoundationAPI} from "../util/AxiosFoundationAPI";
import {useSessionContext} from "../util/SessionContext";
import {classificationByCode} from "../util/objectClassifications";
import {availableModes} from "../util/VisibilityModes";
import EventBus from "../util/EventBus";

/**
 * ONE STABLE LINK SHAPE for a Foundation object, resolved here into the classification route that
 * actually serves it.
 *
 * The server sends "/object/{usid}" in notifications and in the approval queue, and it is right to:
 * it knows the revision it is talking about, and it has no business knowing that this frontend files
 * documents under /documents and contracts under /contracts. Those are the frontend's own furniture
 * and they change without the server hearing about it. So the link the server can honestly write is
 * the one that names the record and nothing else, and the frontend resolves it.
 *
 * A REDIRECT, NOT A RENDER. ObjectDetail already copes with landing on a record of another kind, so
 * rendering it straight from here would work -- but then the address bar would read "/object/..."
 * for the rest of the visit, back-navigation would leave the classification, and the rail would
 * highlight nothing. Bouncing once costs a fetch and leaves the user on the canonical URL, where a
 * bookmark, a reload and the highlighted rail entry all agree about where they are.
 */
export default function ObjectRoute() {
    const {usid} = useParams();
    const navigate = useNavigate();
    const sessionContext = useSessionContext();
    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);
    const [error, setError] = useState<string | null>(null);
    // The view this reader could open the record in, when their active one cannot. Null until the
    // probe below finds one, and null for ever when none does.
    const [elsewhere, setElsewhere] = useState<{mode: string; label: string} | null>(null);
    const [attempt, setAttempt] = useState(0);

    const rowsOf = (response: any) => {
        const list = response?.data?.PlatformMessage?.ListOfObjectRevision?.ObjectRevision ?? [];
        return Array.isArray(list) ? list : [list].filter(Boolean);
    };

    /**
     * WHICH OF THIS READER'S OTHER VIEWS OPENS THIS RECORD, if any.
     *
     * A quality manager owns no records: her position is a hat, not a filing cabinet, and the
     * software items she is accountable for belong to the team member's position beside her. Her
     * role grants `persona:organization`, so Organization view shows her the whole department --
     * and in Position view, which is where everybody starts, this page answered "That record could
     * not be opened." and stopped. Measured in Alpha Medical: Position and Manager find nothing,
     * Organization finds all six software items.
     *
     * THE DEAD END WAS THE DEFECT, NOT THE REFUSAL. Nothing here widens anybody's access: each
     * probe is a read the reader could make by hand with the selector on the list page, refused by
     * the same server-side rule if it does not hold the grant. What it adds is the sentence that
     * page could not say -- you hold a view that opens this -- and a button that takes it.
     *
     * NARROWEST FIRST. `availableModes` lists widest first, for a selector where the widest reading
     * is the one people reach for. Here the useful answer is the SMALLEST view that works, so the
     * reader is not pushed into the organization-wide reading to open one record.
     *
     * AND SILENCE WHEN THERE IS NONE. A record this person cannot see under any view they hold is
     * the stranger's case, and the message stays exactly what it was -- the same words a record
     * that does not exist gets, which is what stops this page becoming a way to ask whether a
     * record is there.
     */
    const probeOtherViews = useCallback(async () => {
        const active = sessionContext.getActiveVisibility();
        if (!active?.Mode) return null;

        const others = availableModes(active.Mode, sessionContext.getPrivileges(),
                                      sessionContext.getAccount())
            .filter((one: {mode: string}) => one.mode !== active.Mode)
            .reverse();

        for (const one of others) {
            const response = await api.get("t1/v1/object_revisions", {
                params: {USID: usid},
                headers: {"visibility-mode": one.mode, "visibility-value": active.Value ?? ""},
            }).catch(() => null);
            if (rowsOf(response).length) return {mode: one.mode, label: one.label};
        }
        return null;
    }, [api, sessionContext, usid]);

    useEffect(() => {
        let cancelled = false;
        if (!usid) return;

        const refuse = async () => {
            const found = await probeOtherViews();
            if (cancelled) return;
            setElsewhere(found);
            setError("That record could not be opened.");
        };

        api.get("t1/v1/object_revisions", {params: {USID: usid}})
            .then(response => {
                if (cancelled) return;
                const rows = rowsOf(response);
                const classification = classificationByCode(rows[0]?.ObjectClassification);
                if (!rows.length || !classification) {
                    // Visible to this user or not, we say the same thing: a link that resolves for
                    // some readers and 404s for others still tells the ones it refuses that the
                    // record exists. The one reader we DO say more to is the one who holds another
                    // view of their own that opens it, which tells them nothing they could not find
                    // by changing the selector themselves.
                    void refuse();
                    return;
                }
                navigate(`${classification.route}/${usid}`, {replace: true});
            })
            .catch(() => { if (!cancelled) void refuse(); });

        return () => { cancelled = true; };
    }, [api, usid, navigate, probeOtherViews, attempt]);

    const switchView = () => {
        if (!elsewhere) return;
        const active = sessionContext.getActiveVisibility();
        sessionContext.setActiveVisibility(elsewhere.mode, active?.Value ?? "");
        EventBus.visibility.emit("change-visibility", sessionContext.getActiveVisibility());
        setError(null);
        setElsewhere(null);
        // RESOLVE AGAIN RATHER THAN NAVIGATE BLIND. The record is what tells us which route serves
        // it, and that read has not been made under the new view yet.
        setAttempt(previous => previous + 1);
    };

    return (
        <div style={{display: "flex", flexDirection: "column", alignItems: "center",
                     justifyContent: "center", gap: 10, padding: 60}}>
            {error
                ? <>
                    <Typography style={{fontSize: 14}}>{error}</Typography>
                    {elsewhere ? (
                        <>
                            <Typography style={{fontSize: 13, color: "#667085", maxWidth: 520,
                                                textAlign: "center"}}>
                                You are reading as{" "}
                                {sessionContext.getActiveVisibility()?.Mode ?? "your position"}.
                                This record is visible in your {elsewhere.label} view.
                            </Typography>
                            <Button size="small" variant="outlined" onClick={switchView}>
                                Switch to {elsewhere.label} view
                            </Button>
                        </>
                    ) : null}
                  </>
                : <CircularProgress size={22}/>}
        </div>
    );
}
